<template>
  <div class="question-answer-tree" :class="displayMode">
    <template v-if="displayMode === 'candidate-answer'">
      <div v-for="(group, index) in candidateQuestionGroups" :key="group.root.id" class="candidate-question-group">
        <QuestionAnswerNode
          :question="group.root"
          :questions="normalizedQuestions"
          :question-number="index + 1"
          :question-map="questionMap"
          :placement-map="placementMap"
          :display-mode="displayMode"
          :autosize="autosize"
          :error-message-formatter="errorMessageFormatter"
          :render-children="false"
          @answer-change="$emit('answer-change', ...arguments)"
          @file-upload="$emit('file-upload', ...arguments)"
          @register-question="$emit('register-question', ...arguments)"
          @unregister-question="$emit('unregister-question', ...arguments)"
        />
        <QuestionAnswerNode
          v-for="child in group.children"
          :key="child.id"
          :question="child"
          :questions="normalizedQuestions"
          :question-map="questionMap"
          :placement-map="placementMap"
          :display-mode="displayMode"
          :autosize="autosize"
          :error-message-formatter="errorMessageFormatter"
          :render-children="false"
          @answer-change="$emit('answer-change', ...arguments)"
          @file-upload="$emit('file-upload', ...arguments)"
          @register-question="$emit('register-question', ...arguments)"
          @unregister-question="$emit('unregister-question', ...arguments)"
        />
      </div>
    </template>
    <template v-else>
      <QuestionAnswerNode
        v-for="(question, index) in rootQuestions"
        :key="question.id"
        :question="question"
        :questions="normalizedQuestions"
        :question-number="index + 1"
        :question-map="questionMap"
        :placement-map="placementMap"
        :display-mode="displayMode"
        :autosize="autosize"
        :error-message-formatter="errorMessageFormatter"
        @answer-change="$emit('answer-change', ...arguments)"
        @file-upload="$emit('file-upload', ...arguments)"
        @register-question="$emit('register-question', ...arguments)"
        @unregister-question="$emit('unregister-question', ...arguments)"
      />
    </template>
  </div>
</template>

<script>
import _ from 'lodash'
import QuestionAnswerNode from './questionAnswerNode.vue'

export default {
  name: 'QuestionAnswerTree',
  components: { QuestionAnswerNode },
  props: {
    questions: {
      type: Array,
      default: () => []
    },
    allQuestions: {
      type: Array,
      default: () => []
    },
    displayMode: {
      type: String,
      required: true
    },
    autosize: {
      type: Object,
      default: null
    },
    errorMessageFormatter: {
      type: Function,
      default: value => value
    }
  },
  computed: {
    normalizedQuestions () {
      return Array.isArray(this.questions) ? this.questions : []
    },
    normalizedAllQuestions () {
      return Array.isArray(this.allQuestions) ? this.allQuestions : []
    },
    questionMap () {
      return _.keyBy(this.normalizedQuestions, question => String(question.id))
    },
    placementMap () {
      return this.keepRenderablePlacements(this.normalizedQuestions, this.buildQuestionPlacementMap(this.normalizedAllQuestions))
    },
    rootQuestions () {
      return this.normalizedQuestions.filter(question => {
        return !this.placementMap[String(question.id)]
      })
    },
    candidateQuestionGroups () {
      return this.rootQuestions.map(root => ({
        root,
        children: this.normalizedQuestions.filter(question => {
          return String(question.id) !== String(root.id) && this.findRootQuestionId(question.id) === String(root.id)
        })
      }))
    }
  },
  methods: {
    hasValue (value) {
      return value !== null && value !== undefined && value !== ''
    },
    buildQuestionPlacementMap (questions) {
      const candidatesByChild = {}

      // Editing placement follows only the current parent option configuration.
      // Historical child relationship fields must not revive removed or moved edges.
      questions.forEach(parentQuestion => {
        ;(_.get(parentQuestion, 'treeMeta.optionTreeMap') || []).forEach(option => {
          if (!this.hasValue(option.nextQuestionId)) return
          const childKey = String(option.nextQuestionId)
          const edgeKey = `${parentQuestion.id}:${option.id}`
          if (!candidatesByChild[childKey]) candidatesByChild[childKey] = {}
          candidatesByChild[childKey][edgeKey] = {
            parentQuestionId: String(parentQuestion.id),
            parentOptionId: String(option.id)
          }
        })
      })
      return Object.keys(candidatesByChild).reduce((placementMap, childKey) => {
        const candidates = Object.values(candidatesByChild[childKey])
        if (candidates.length === 1) placementMap[childKey] = candidates[0]
        return placementMap
      }, {})
    },
    keepRenderablePlacements (questions, placementMap) {
      const questionMap = _.keyBy(questions, question => String(question.id))
      const relationExists = questionId => {
        const placement = placementMap[questionId]
        if (!placement) return false
        const parent = questionMap[placement.parentQuestionId]
        return Boolean(parent && (parent.optionList || []).some(option => String(option.value) === placement.parentOptionId))
      }
      const hasInvalidAncestry = questionId => {
        const visited = new Set([questionId])
        let currentId = questionId
        while (placementMap[currentId]) {
          if (!relationExists(currentId)) return true
          currentId = placementMap[currentId].parentQuestionId
          if (visited.has(currentId)) return true
          visited.add(currentId)
        }
        return false
      }
      return Object.keys(placementMap).reduce((result, questionId) => {
        if (questionMap[questionId] && !hasInvalidAncestry(questionId)) {
          result[questionId] = placementMap[questionId]
        }
        return result
      }, {})
    },
    findRootQuestionId (questionId) {
      let currentId = String(questionId)
      const visited = new Set([currentId])
      while (this.placementMap[currentId]) {
        currentId = this.placementMap[currentId].parentQuestionId
        if (visited.has(currentId)) return String(questionId)
        visited.add(currentId)
      }
      return currentId
    }
  }
}
</script>

<style lang="scss" scoped>
.question-answer-tree {
  min-width: 0;
}
</style>
